import React from 'react';

import { AnalogLink, AnalogLinkEndResult, ResultCurve } from '@paragon/contract-library';
import { ReadonlyRecord } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { solveForceVectorProof } from '../../library/geometryUtility/ForceVectorProofSolverUtility';
import { areAnyItemsOfTypeSelected } from '../../library/selection/selectors';
import { RootState } from '../../store';
import {
  getDeflectionCurvesForSelectedLoadCaseAndSelectedItem,
  getJointResultsForSelectedLoadCaseAndSelectedItem,
  getLinkEndResultsForSelectedLoadCaseAndSelectedItem,
  getMomentCurvesForSelectedLoadCaseAndSelectedItem,
  getPlatingForcesForSelectedLoadCaseAndSelectedItem,
  getShearCurvesForSelectedLoadCaseAndSelectedItem,
} from '../../store/selectors/componentForcesSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getAnalogLinks } from '../../store/selectors/getAnalogLinks';
import { AxialTransverseLoadBox } from './AxialAndTransverseLoadBox';
import { DeflectionSplineVisual } from './DeflectionSplineVisual';
import { LinkEndResultVisual } from './LinkEndResultVisual';
import { PlatingForceResultVisual } from './PlatingForceResultVisual';
import { ResultCurveType, ResultCurveVisual } from './ResultCurveVisual';

export const AnalysisResultContainer = () => (
  <>
    <Curves
      displayedForceName="shearCurves"
      selector={getShearCurvesForSelectedLoadCaseAndSelectedItem}
      curveType={ResultCurveType.ShearCurve}
      scale={0.01}
    />
    <Curves
      displayedForceName="momentCurves"
      selector={getMomentCurvesForSelectedLoadCaseAndSelectedItem}
      curveType={ResultCurveType.MomentCurve}
      scale={0.005}
    />
    <Curves
      displayedForceName="deflectionLinkCurves"
      selector={getDeflectionCurvesForSelectedLoadCaseAndSelectedItem}
      curveType={ResultCurveType.DeflectionCurve}
      scale={10}
    />
    <DeflectionSplines />
    <PlatingForces />
    <BearingReactions />
    <LocalLinkEndForces />
    <GlobalLinkEndForces />
    <ForceProof />
  </>
);

function createLinkEndResultData(links: ReadonlyRecord<string, AnalogLink>, linkEndResult: AnalogLinkEndResult) {
  const link = links[linkEndResult.analogLinkGuid];
  if (!link) throw new Error('Could Not find analog link for link end result');
  const { x, y, z } = link.iJoint.guid === linkEndResult.analogJointGuid ? link.iJoint : link.jJoint;
  return {
    position: { x, y, z },
    globalForceVector: { x: linkEndResult.xForce, y: linkEndResult.yForce },
    axialForce: linkEndResult.axialForce,
    moment: linkEndResult.moment,
  };
}

const useAnalogJoints = () => {
  const analogModel = useAppSelector(getActiveComponentDesign)?.analogModel;
  if (!analogModel) return {};
  return Object.fromEntries(analogModel.analogJoints.map((joint) => [joint.guid, joint]));
};

const Curves = (props: {
  displayedForceName: string;
  selector: (state: RootState) => readonly ResultCurve[];
  curveType: ResultCurveType;
  scale: number;
}) => {
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const shearCurves = useAppSelector(props.selector);
  const analogLinks = useAppSelector(getAnalogLinks);

  if (!displayedForces.includes(props.displayedForceName) || !analogLinks) return null;

  return shearCurves.map((curve) => {
    const link = analogLinks[curve.analogLinkGuid];
    if (!link) throw new Error('Could not find analog link for link result curve');
    return (
      <ResultCurveVisual
        key={curve.guid}
        linkSegment={{ start: link.iJoint, end: link.jJoint }}
        coefficients={curve.coefficients}
        linkResultType={props.curveType}
        scale={props.scale}
      />
    );
  });
};

const DeflectionSplines = () => {
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const deflectionSplines = useAppSelector(getDeflectionCurvesForSelectedLoadCaseAndSelectedItem);
  const analogLinks = useAppSelector(getAnalogLinks);

  if (!displayedForces.includes('deflectionCurves') || !analogLinks) return null;

  return deflectionSplines.map((curve) => {
    const link = analogLinks[curve.analogLinkGuid];
    if (!link) throw new Error('Could Not find analog link for link result curve');
    return (
      <DeflectionSplineVisual
        key={curve.guid}
        linkSegment={{ start: link.iJoint, end: link.jJoint }}
        coefficients={curve.coefficients}
        scale={10}
      />
    );
  });
};

const PlatingForces = () => {
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const platingForces = useAppSelector(getPlatingForcesForSelectedLoadCaseAndSelectedItem);
  const analogJoints = useAnalogJoints();

  if (!displayedForces.includes('platingForces')) return null;

  return platingForces.map((plateForceResult) => {
    const joint = analogJoints[plateForceResult.analogJointGuid];
    if (!joint) throw new Error('Could Not find analog joint for plating force result');
    const position = { x: joint.x, y: joint.y, z: joint.z };
    return (
      <PlatingForceResultVisual
        key={plateForceResult.guid}
        position={position}
        globalForceVector={{
          x: plateForceResult.xForce,
          y: plateForceResult.yForce,
        }}
        platingForceType={plateForceResult.platingForceType}
        moment={plateForceResult.moment}
      />
    );
  });
};

const BearingReactions = () => {
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const bearingReactions = useAppSelector(getJointResultsForSelectedLoadCaseAndSelectedItem);
  const analogJoints = useAnalogJoints();

  if (!displayedForces.includes('bearingReactions')) return null;

  return bearingReactions.map((reaction) => {
    const joint = analogJoints[reaction.analogJointGuid];
    if (!joint) throw new Error('Could not find analog joint for bearing reaction.');
    const position = { x: joint.x, y: joint.y, z: joint.z };
    return (
      <LinkEndResultVisual
        key={reaction.guid}
        position={position}
        globalForceVector={{
          x: reaction.xForce,
          y: reaction.yForce,
        }}
        axialForce={0}
        moment={reaction.moment}
      />
    );
  });
};

const LocalLinkEndForces = () => {
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const linkEndForces = useAppSelector(getLinkEndResultsForSelectedLoadCaseAndSelectedItem);
  const analogLinks = useAppSelector(getAnalogLinks);

  if (!displayedForces.includes('localLinkEndForces') || !analogLinks) return null;

  return linkEndForces.map((linkEndResult) => {
    const link = analogLinks[linkEndResult.analogLinkGuid];
    if (!link) throw new Error('Could Not find analog link for link end result');
    const iJoint = { x: link.iJoint.x, y: link.iJoint.y, z: link.iJoint.z };
    const jJoint = { x: link.jJoint.x, y: link.jJoint.y, z: link.jJoint.z };
    const position = link.jJoint.guid === linkEndResult.analogJointGuid ? jJoint : iJoint;
    return (
      <AxialTransverseLoadBox
        key={linkEndResult.guid}
        globalForceVector={{
          x: linkEndResult.xForce,
          y: linkEndResult.yForce,
        }}
        axialForce={linkEndResult.axialForce}
        transverseForce={linkEndResult.transverseForce}
        linkSegment={{
          start: {
            x: iJoint.x,
            y: iJoint.y,
            z: iJoint.z,
          },
          end: {
            x: jJoint.x,
            y: jJoint.y,
            z: jJoint.z,
          },
        }}
        position={position}
      />
    );
  });
};

const GlobalLinkEndForces = () => {
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const linkEndForces = useAppSelector(getLinkEndResultsForSelectedLoadCaseAndSelectedItem);
  const analogLinks = useAppSelector(getAnalogLinks);

  if (!displayedForces.includes('linkEndForces') || !analogLinks) return null;

  return linkEndForces.map((linkEndResult) => {
    const props = createLinkEndResultData(analogLinks, linkEndResult);
    return <LinkEndResultVisual key={linkEndResult.guid} {...props} />;
  });
};

const ForceProof = () => {
  const analogModel = useAppSelector(getActiveComponentDesign)?.analogModel;
  const displayedForces = useAppSelector((state) => state.visualOptions.displayedForces);
  const linkEndResults = useAppSelector(getLinkEndResultsForSelectedLoadCaseAndSelectedItem);
  const anyLinksSelected = useAppSelector((state) => areAnyItemsOfTypeSelected('analogLink', state));
  const analogLinks = useAppSelector(getAnalogLinks);

  if (!displayedForces.includes('forceProof') || anyLinksSelected || !analogModel || !analogLinks) return null;

  const jointResultGuidObject: { [guid: string]: AnalogLinkEndResult[] } = {};
  for (const linkEndResult of linkEndResults) {
    if (!jointResultGuidObject[linkEndResult.analogJointGuid])
      jointResultGuidObject[linkEndResult.analogJointGuid] = [];
    jointResultGuidObject[linkEndResult.analogJointGuid].push(linkEndResult);
  }

  const linkEndResultsSorted = [];
  for (const joint of analogModel.analogJoints) {
    if (!jointResultGuidObject[joint.guid]) continue;

    const linkEndData = jointResultGuidObject[joint.guid].map((linkEndResult) =>
      createLinkEndResultData(analogLinks, linkEndResult),
    );
    const solvedForceProofData = solveForceVectorProof(linkEndData, joint);
    let index = 0;
    for (const forcePositionInfo of solvedForceProofData) {
      const data = linkEndData[forcePositionInfo.index];
      data.position = forcePositionInfo.position;
      linkEndResultsSorted.push(<LinkEndResultVisual key={`${joint.guid}-${index}`} {...data} />);
      index += 1;
    }
    delete jointResultGuidObject[joint.guid];
  }

  if (Object.keys(jointResultGuidObject).length > 0) throw new Error('Joint guid object was not empty');

  return linkEndResultsSorted;
};
